import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import MatchesDrillDownModal from './MatchesDrillDownModal';

const C = ['#0B3D91', '#E31837', '#2196F3', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#4FC3F7'];

export default function MatchesSection({ data }) {
  const [view, setView] = useState('series');
  const [matchDrill, setMatchDrill] = useState(null);
  const { match_summary, series_data, _matchUmpires, _grounds, _clubs, _matches: rawMatches } = data;

  const seriesData = (series_data || []).slice(0, 15);
  const groundsChart = (_grounds || []).slice(0, 15).map(g => ({ name: g.name, matches: g.match_count }));

  const views = [
    { id: 'series', label: 'Series & Leagues' },
    { id: 'grounds', label: `Grounds (${match_summary?.total_grounds || 0})` },
    { id: 'clubs', label: `Clubs (${match_summary?.total_clubs || 0})` },
    { id: 'umpires', label: `Umpires (${match_summary?.total_umpires || 0})` },
  ];

  return (
    <div data-testid="matches-section">
      <div className="section-title">Matches, Grounds & Officials</div>

      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '1.25rem' }}>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value">{(match_summary?.total_matches || 0).toLocaleString()}</div>
          <div className="kpi-label">Matches Played</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#E31837' }}>{match_summary?.total_grounds || 0}</div>
          <div className="kpi-label">Cricket Grounds</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#2196F3' }}>{match_summary?.total_clubs || 0}</div>
          <div className="kpi-label">Clubs & Teams</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#10B981' }}>{match_summary?.total_umpires || 0}</div>
          <div className="kpi-label">Active Umpires</div>
        </div>
      </div>

      <div className="people-pills" style={{ marginBottom: '1rem' }}>
        {views.map(v => (
          <button key={v.id} className={`people-pill ${view === v.id ? 'active' : ''}`}
            onClick={() => setView(v.id)} data-testid={`match-filter-${v.id}`}>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'series' && (
        <>
          <div className="glass-card chart-card">
            <h3>Matches by Series / League</h3>
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={seriesData} layout="vertical" margin={{ left: 220 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="series" tick={{ fontSize: 10 }} width={220} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="matches" name="Matches" radius={[0, 6, 6, 0]}>
                  {seriesData.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="glass-card" style={{ padding: '0', marginTop: '1.25rem', overflow: 'auto' }}>
            <table className="data-table" data-testid="series-table">
              <thead>
                <tr><th>#</th><th>Series / League</th><th>Category</th><th className="num">Matches</th></tr>
              </thead>
              <tbody>
                {(series_data || []).map((s, i) => (
                  <tr
                    key={i}
                    className="drill-row-clickable"
                    onClick={() => setMatchDrill({ variant: 'series', seriesRow: { series: s.series, sheet: s.sheet } })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setMatchDrill({ variant: 'series', seriesRow: { series: s.series, sheet: s.sheet } });
                      }
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{s.series}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{s.sheet}</td>
                    <td className="num" style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>{s.matches}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'grounds' && (
        <>
          <div className="glass-card chart-card">
            <h3>Matches by Ground</h3>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={groundsChart} margin={{ bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, angle: -35 }} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="matches" name="Matches" radius={[6, 6, 0, 0]}>
                  {groundsChart.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="glass-card" style={{ padding: '0', marginTop: '1.25rem', overflow: 'auto' }}>
            <table className="data-table" data-testid="grounds-table">
              <thead>
                <tr><th>#</th><th>Ground</th><th className="num">Matches Hosted</th></tr>
              </thead>
              <tbody>
                {(_grounds || []).map((g, i) => (
                  <tr
                    key={i}
                    className="drill-row-clickable"
                    onClick={() => setMatchDrill({ variant: 'ground', groundRow: g })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setMatchDrill({ variant: 'ground', groundRow: g });
                      }
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{g.name}</td>
                    <td className="num" style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>{g.match_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'clubs' && (
        <div className="glass-card" style={{ padding: '0', overflow: 'auto' }}>
          <div style={{ padding: '1rem 1.25rem 0' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700 }}>Clubs & Teams</h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
              All clubs and teams participating in competitive cricket
            </p>
          </div>
          <table className="data-table" data-testid="clubs-table">
            <thead>
              <tr><th>#</th><th>Club / Team</th><th className="num">Match Appearances</th></tr>
            </thead>
            <tbody>
              {(_clubs || []).slice(0, 50).map((c, i) => (
                <tr
                  key={i}
                  className="drill-row-clickable"
                  onClick={() => setMatchDrill({ variant: 'club', clubRow: c })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setMatchDrill({ variant: 'club', clubRow: c });
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{c.appearances}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'umpires' && (
        <div className="glass-card" style={{ padding: '0', overflow: 'auto' }}>
          <div style={{ padding: '1rem 1.25rem 0' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700 }}>Umpire Leaderboard</h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
              Active match officials ranked by matches officiated
            </p>
          </div>
          <table className="data-table" data-testid="umpires-table">
            <thead>
              <tr><th>#</th><th>Umpire</th><th>Level</th><th className="num">Matches</th></tr>
            </thead>
            <tbody>
              {(_matchUmpires || []).map((u, i) => (
                <tr
                  key={i}
                  className="drill-row-clickable"
                  onClick={() => setMatchDrill({ variant: 'umpire', umpireRow: u })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setMatchDrill({ variant: 'umpire', umpireRow: u });
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td>
                    {u.level ? (
                      <span style={{
                        padding: '0.15rem 0.5rem', borderRadius: 12,
                        fontSize: '0.72rem', fontWeight: 600,
                        background: u.level.includes('3') ? 'rgba(11,61,145,0.1)' :
                          u.level.includes('2') ? 'rgba(33,150,243,0.1)' : 'rgba(79,195,247,0.15)',
                        color: u.level.includes('3') ? '#0B3D91' :
                          u.level.includes('2') ? '#2196F3' : '#0284C7',
                      }}>{u.level}</span>
                    ) : '\u2014'}
                  </td>
                  <td className="num" style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>{u.match_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MatchesDrillDownModal
        open={matchDrill != null}
        onClose={() => setMatchDrill(null)}
        variant={matchDrill?.variant ?? null}
        seriesRow={matchDrill?.seriesRow}
        groundRow={matchDrill?.groundRow}
        clubRow={matchDrill?.clubRow}
        umpireRow={matchDrill?.umpireRow}
        matches={rawMatches || []}
      />
    </div>
  );
}
